import { CartModel, CartItemModel } from "models/cart-model";
import { CART_ID, upsellsRemovedKey } from "utils/constants";

export const getExistingCartId = () => localStorage.getItem(CART_ID);

export const getOrCreateCartId = async (shopifyClient) => {
	const cartId = getExistingCartId();
	if (cartId) return cartId;

	const cart = await shopifyClient.cart.create();

	localStorage.setItem(CART_ID, cart.id);

	return cart.id;
};

export const mapCart = (cart) => {
	const lineItems = cart.lines?.nodes || [];
	const createdAt = new Date(cart.createdAt);
	const totalPrice = cart.cost.subtotalAmount;
	const checkoutUrl = cart.checkoutUrl;
	const cartId = cart.id;

	let totalQuantity = 0,
		totalProgress = 0;

	const freeGifts = {};

	const items = Object.fromEntries(lineItems?.filter((item) => item.variant).map((item) => {
		const discounts = [];
		const variant = item.variant;
		const product = variant.product;
		let itemWeight = variant.weightUnit === "GRAMS" ? parseFloat(variant.weight / 1000) : variant.weight;
		if (product.productType === "Gift Card" && lineItems.length > 1 || itemWeight === 0.001) {
			itemWeight = 0;
		}

		const price = +variant.price.amount;
		const compareAtPrice = Math.max(+variant.compareAtPrice?.amount || 0, price) ?? price;

		const totalPrice = (compareAtPrice ?? price) * item.quantity;
		let totalDiscountedPrice = price * item.quantity;

		const itemDiscounts = item.discountAllocations;
		totalQuantity += item?.quantity;
		totalProgress += itemWeight * item.quantity;
		let itemDiscountsAmount = 0;
		if (itemDiscounts.length) {
			itemDiscounts.forEach(discount => {
				const amount = parseFloat(discount.discountedAmount?.amount);
				discounts.push({
					title: discount?.title ?? discount?.code ?? `- ${amount}`,
					amount,
				});
				if (amount) itemDiscountsAmount += amount;
			});
			totalDiscountedPrice -= itemDiscountsAmount;
		}

		if (product.freeGifts && product.freeGifts.references) {
			product.freeGifts.references.nodes.map(gift => {
				const giftVariant = gift.variants.nodes[0];
				const freeGift = {
					title: gift.title,
					id: gift.id,
					image: giftVariant.image,
					variant: {
						id: giftVariant.id,
						price: parseFloat(giftVariant.price.amount),
					},
					quantity: item.quantity,
					disableInputs: true,
				};
				if (freeGifts[giftVariant.id]) {
					freeGifts[giftVariant.id].quantity += item.quantity;
				} else {
					freeGifts[giftVariant.id] = freeGift;
				}
			});
		}

		const cartItem = new CartItemModel({
			id: product.id,
			tempId: item.id,
			title: product.title,
			handle: product.handle,
			quantity: item.quantity,
			image: { ...variant.image, altText: product.title },
			discounts,
			type: product.productType,
			oneTime: Boolean(item.attributes?.find(attr => attr.key === "_oneTime") || false),
			sellingPlan: item.sellingPlanAllocation?.sellingPlan,
			variant: {
				available: variant.available,
				sellingPlanAllocations: item.variant.sellingPlanAllocations.nodes,
				id: variant.id,
				sku: variant.sku,
				title: variant.title,
				price,
				totalPrice,
				compareAtPrice,
				weight: itemWeight,
				discountedPrice: +(totalDiscountedPrice / item.quantity).toFixed(2),
				totalDiscountedPrice,
			},
		});

		return [variant.id, cartItem];
	}),
	);

	return new CartModel({
		id: cartId,
		items,
		createdAt,
		attributes: cart.attributes,
		totalPrice: parseFloat(totalPrice?.amount),
		checkoutUrl,
		totalQuantity,
		totalProgress,
		freeGifts: Object.values(freeGifts),
		discountCodes: cart.discountCodes.map(discount => discount.code),
		allGiftCards: lineItems?.length > 0 && lineItems.every((item) => item.variant?.product?.productType === "Gift Card"),
		loading: false,
		error: false,
	});
};

// Function to separate the cart items into core and side products (add-ons)
export const getCartItemsSeparated = (items) => {
	let coreProducts = [], sideProducts = [];

	items.forEach(item => {
		// Add-ons are always side products as well as items with weight 1g (0.001kg)
		// Type is not available for the account cart, so we use weightInGrams
		if (item.duplicates) {
			item.duplicates.forEach(duplicate => {
				if (duplicate.discountedPrice !== item.discountedPrice &&
					(duplicate.discountedPrice === 0 || item.discountedPrice === 0)
				) {
					(item.type === "Add-on" || item.weightInGrams === 1)
						? sideProducts.push(duplicate)
						: coreProducts.push(duplicate);
				}
			});
		}

		if (item.type === "Add-on" || item.weightInGrams === 1)
			return sideProducts.push(item);

		coreProducts.push(item);
	});

	return { coreProducts, sideProducts };
};

// When a side product is removed, add it to the session storage to prevent it from showing again
export const onSideProductRemoved = (variantId) => {
	// Get the removed upsells from local storage
	const upsellStorage = JSON.parse(sessionStorage.getItem(upsellsRemovedKey)) || [];
	// Add the current upsells (uniques) to the list
	sessionStorage.setItem(upsellsRemovedKey, JSON.stringify([...new Set([...upsellStorage, `${variantId}`])]));
};

export const calculateBowlsPrice = (items) => items.reduce((acc, item) => {
	// Do not count items without weight
	if (!item?.variant || !item?.variant?.weight) return acc;

	return acc + item.quantity * item.variant.price;
}, 0);

// Calculate the price of all items in the cart for the active box
export const calculateTotalBoxPrice = (items, discount) =>
	items.reduce((acc, item) => {
		if (!item?.variant) return acc;

		if (item?.variant?.weight) {
			const boxDiscount = discount * item.variant.weight;
			return acc + item.quantity * (item.variant.price - boxDiscount);
		}

		// If item has no weight, use totalDiscountedPrice
		return acc + item?.variant?.totalDiscountedPrice;
	}, 0);

// Calculate total for all items in the cart without weight or that are one-time purchases
export const calculateTotalNoWeightPrice = (items) => items.reduce((acc, item) => {
	if (!item?.variant || item?.variant?.weight || item?.variant?.sellingPlanAllocations?.length) return acc;

	return acc + item.variant.totalDiscountedPrice;
}, 0);

// Function to find the closest box based on totalWeight
export const findClosestBox = (boxes, totalWeight) => {
	const sortedBoxes = boxes.slice().sort((a, b) => b.size.value - a.size.value);

	const closestBox = sortedBoxes.find(box => box.size.value <= totalWeight);

	return closestBox ?? sortedBoxes[sortedBoxes.length - 1];
};

export const getHighestBoxPrice = (boxes) => Math.max(...boxes.map(box => box.price));

export const getBoxPriceDetails = (activeBox, boxes, discountPercentage, shipping) => {
	const highestBoxPrice = getHighestBoxPrice(boxes);
	const boxValue = activeBox?.size?.value || 0;
	const totalBoxPrice = boxValue * highestBoxPrice;
	const totalBowlsPrice = boxValue * activeBox?.price || 0;
	const shippingCost = boxValue <= 6 ? shipping : 0;
	const totalOriginalPrice = totalBoxPrice + shippingCost;
	const discountedPrice = totalBowlsPrice + shippingCost;

	let firstOrderDiscount = 0;
	if (discountPercentage) {
		firstOrderDiscount = Math.ceil((discountPercentage / 100 * totalBowlsPrice) * 100) / 100;
	}
	const totalDiscountedPrice = discountedPrice - firstOrderDiscount;

	const savings = totalOriginalPrice - totalDiscountedPrice;

	return {
		highestBoxPrice,
		totalBoxPrice,
		totalBowlsPrice,
		savings,
		totalOriginalPrice,
		totalDiscountedPrice,
		firstOrderDiscount
	};
};